// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ProductBrand, { ProductBrandStyles } from "@flip/Overrides/Components/ProductBrand";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@flip/Overrides/Components/ProductPartNumbers";
import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import sendProductRecommendationTracking from "@insite/client-framework/Store/Components/ProductRecommendations/Handlers/SendProductRecommendationTracking";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { ProductAddToListLinkStyles } from "@insite/content-library/Components/ProductAddToListLink";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductImageBadges from "@insite/content-library/Components/ProductImageBadges";
import ProductTextBadges from "@insite/content-library/Components/ProductTextBadges";
import Badge from "@insite/mobius/Badge";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import React, { useState } from "react";

import LegacyManufacturer, { LegacyManufacturerStyles } from "@flip/App/Components/Product/LegacyManufacturer";
import ProductGridContainer, { ProductGridContainerStyle } from "@flip/App/Components/Product/ProductGridTemplate";
import ProductPreviousPurchase from "@flip/App/Components/Product/ProductPreviousPurchase";
import ProductPriceAndActions, {
    ProductPriceAndActionsStyle,
} from "@flip/App/Components/Product/ProductPriceAndActions";
import productDescriptionVariantStyles from "@flip/App/SharedStyle/ProductDescriptionStyles";
import { ProductAvailabilityStyles } from "@flip/Overrides/Components/ProductAvailability";
import { ProductPriceStyles } from "@flip/Overrides/Components/ProductPrice";
import { ellipsis, parseBoolean } from "@flip/XDL/Utilities";
import useOverlayBadge from "@insite/client-framework/Common/Hooks/useOverlayBadge";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { ProductQuantityBreakPricingStyles } from "@insite/content-library/Components/ProductQuantityBreakPricing";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    productInfo: getProductInfoFromList(state, ownProps.carouselId, ownProps.product.id),
    canSeePrices: getSettingsCollection(state).productSettings.canSeePrices,
});

const mapDispatchToProps = {
    updateProductInfo,
    sendProductRecommendationTracking,
};

interface OwnProps {
    carouselId: string;
    product: ProductModel;
    recommendationId?: number;
    showImage: boolean;
    showBrand: boolean;
    showTitle: boolean;
    showPartNumbers: boolean;
    showPrice: boolean;
    showAddToCart: boolean;
    showAddToList: boolean;
    showImageBadges?: boolean;
    maximumNumberOfImageBadges?: number;
    showTextBadges?: boolean;
    maximumNumberOfTextBadges?: number;
    // XNG-Change
    extendedStyles?: ProductCarouselProductStyles;
    actionsTemplate?: (product: ProductModel) => React.ReactNode;
    showAvailability?: boolean;
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ProductCarouselProductStyles {
    productImageStyles?: ProductImageStyles;
    productBrandWrapper?: InjectableCss;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    productPriceWrapper?: InjectableCss;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    productQuantityBreakPricingStyles?: ProductQuantityBreakPricingStyles;
    addToCartContainer?: GridContainerProps;
    qtyGridItem?: GridItemProps;
    productQuantityOrderedStyles?: TextFieldProps;
    addToCartButtonGridItem?: GridItemProps;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    productAddToListLinkStyles?: ProductAddToListLinkStyles;
    badgeAreaWrapper?: InjectableCss;
    badgeTextWrapper?: InjectableCss;
    badgeImageWrapper?: InjectableCss;
    badgeText?: TypographyPresentationProps;
    badgeImageProps?: JSX.IntrinsicElements["img"];
    // XNG-Changes
    imageInfoWrapper?: InjectableCss;
    infoSectionWrapper?: InjectableCss;
    priceAndActionsWrapper?: InjectableCss;
    productPriceStyles?: ProductPriceStyles;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    productPriceAndActionsStyle?: ProductPriceAndActionsStyle;
    productGridContainer?: ProductGridContainerStyle;
    productPreviousPurchase?: LazyImageProps;
    brandWrapper?: InjectableCss;
    // XNG-Changes
    legacyManufacturerStyles?: LegacyManufacturerStyles;
}

export const productCarouselProductStyles: ProductCarouselProductStyles = {
    productImageStyles: {
        linkWrappingImage: {
            css: css`
                position: relative;
                height: 304px;
                width: 100%;
            `,
        },
        img: {
            width: 317,
            height: 304,
            css: css`
                object-fit: contain;
                max-width: 100%;
                max-height: 100%;
                height: auto;
            `,
        },
    },
    productDescriptionStyles: {
        ...productDescriptionVariantStyles.medium,
        productDetailDescription: {
            ...productDescriptionVariantStyles.medium?.productDetailDescription,
            css: css`
                ${productDescriptionVariantStyles.medium?.productDetailDescription?.css}
                ${ellipsis(2)}
            `,
        },
        productDetailLink: {
            ...productDescriptionVariantStyles.medium?.productDetailLink,
            css: css`
                ${productDescriptionVariantStyles.medium?.productDetailLink?.css}
                ${ellipsis(2)}
            `,
        },
    },

    priceAndActionsWrapper: {
        css: css`
            display: flex;
            align-items: flex-end;
            flex-grow: 1;
        `,
    },
    badgeAreaWrapper: {
        css: css`
            position: relative;
        `,
    },
    badgeTextWrapper: {
        css: css`
            position: absolute;
            width: 50%;
            white-space: nowrap;
            overflow: hidden;
        `,
    },
    badgeImageWrapper: {
        css: css`
            position: absolute;
            width: 25%;
            height: 25%;
        `,
    },
    badgeText: {
        weight: "bold",
        css: css`
            width: 100%;
            margin: auto;
            ${wrapInContainerStyles}
        `,
    },
    productPreviousPurchase: {
        css: css`
            position: absolute;
            left: 8px;
            bottom: 8px;
        `,
        mergeCss: true,
    },
    productBrandStyles: {
        nameLink: {
            css: css`
                display: block;
                line-height: 13px;
            `,
        },
    },
    brandWrapper: {
        css: css`
            min-height: 14px;
        `,
    },
};

const ProductCarouselProduct = ({
    carouselId,
    product,
    showImage,
    showBrand,
    showTitle,
    showPartNumbers,
    showPrice,
    showAddToCart,
    showAddToList,
    showImageBadges,
    maximumNumberOfImageBadges,
    showTextBadges,
    maximumNumberOfTextBadges,
    updateProductInfo,
    extendedStyles,
    productInfo,
    recommendationId,
    sendProductRecommendationTracking,
    actionsTemplate,
    showAvailability = true,
}: Props) => {
    const [styles] = useState(() => mergeToNew(productCarouselProductStyles, extendedStyles));

    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: carouselId, productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: carouselId, productId: product.id, unitOfMeasure });
    };

    if (!product || !productInfo) {
        return null;
    }

    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };

    const trackProductRecommendation = (isNewPageWasOpen: boolean) => {
        if (recommendationId) {
            sendProductRecommendationTracking({ recommendationId, isNewPageWasOpen });
        }
    };

    const { showOverlayBadge, overlayBadge, overlayBadgeWrapperStyles } = useOverlayBadge({
        productContext,
        showTextBadge: showTextBadges,
        showImageBadge: showImageBadges,
        styles,
    });

    const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges, showImageBadges });

    return (
        <>
            <ProductContext.Provider value={productContext}>
                <ProductGridContainer
                    extendedStyles={styles.productGridContainer}
                    id={`product_${product.id}`}
                    data-test-selector="productContainer"
                >
                    <StyledWrapper {...styles.imageInfoWrapper}>
                        <ProductGridContainer.Image>
                            {showImage && !showOverlayBadge && (
                                <ProductImage
                                    product={productContext}
                                    onClick={() => trackProductRecommendation(true)}
                                    extendedStyles={styles.productImageStyles}
                                />
                            )}
                            {showImage && showOverlayBadge && overlayBadge && (
                                <StyledWrapper {...styles.badgeAreaWrapper} data-test-selector="productCarouselBadge">
                                    <ProductImage extendedStyles={styles.productImageStyles} product={productContext} />
                                    <StyledWrapper {...overlayBadgeWrapperStyles}>
                                        <Badge
                                            type={overlayBadge.badgeType}
                                            value={
                                                overlayBadge.badgeType === "Image"
                                                    ? overlayBadge.largeImageBadgePath
                                                    : overlayBadge.displayText
                                            }
                                            badgeTextColor={overlayBadge.textColorHexCode}
                                            badgeBackgroundColor={overlayBadge.badgeColorHexCode}
                                            badgeTextRounding={overlayBadge.badgeStyle === "Round" ? 20 : 0}
                                            imageAltText={
                                                overlayBadge.imageAltText
                                                    ? overlayBadge.imageAltText
                                                    : overlayBadge.name
                                            }
                                            textBadgeCss={styles.badgeText}
                                            imageBadgeProps={styles.badgeImageProps}
                                        />
                                    </StyledWrapper>
                                </StyledWrapper>
                            )}
                            <ProductPreviousPurchase
                                isPreviouslyPurchased={product.properties.isPreviouslyPurchased}
                                extendedStyles={styles.productPreviousPurchase}
                            />
                        </ProductGridContainer.Image>
                        <ProductGridContainer.Info>
                            <StyledWrapper {...styles.infoSectionWrapper}>
                                {showBrand && (
                                    <StyledWrapper {...styles.brandWrapper}>
                                        {/* XNG-Changes: FLIP-507 */}
                                        {product.brand && (
                                            <ProductBrand
                                                brand={product.brand}
                                                isAuthorized={parseBoolean(product.properties.isAuthorizedBrand)}
                                                extendedStyles={styles.productBrandStyles}
                                            />
                                        )}
                                    </StyledWrapper>
                                )}

                                {showTitle && (
                                    <ProductDescription
                                        product={productContext}
                                        onClick={() => trackProductRecommendation(true)}
                                        extendedStyles={productDescriptionVariantStyles.small}
                                    />
                                )}
                                {showPartNumbers && (
                                    <ProductPartNumbers
                                        productNumber={product.productNumber}
                                        customerProductNumber={product.customerProductNumber}
                                        manufacturerItem={product.manufacturerItem}
                                        sku={product.detail?.sku}
                                        extendedStyles={styles.productPartNumbersStyles}
                                    />
                                )}

                                {showTextBadges && textBadges && (
                                    <ProductTextBadges
                                        badgeList={textBadges}
                                        maxBadges={maximumNumberOfTextBadges ?? 4}
                                    />
                                )}
                                {showImageBadges && imageBadges && (
                                    <ProductImageBadges
                                        badgeList={imageBadges}
                                        maxBadges={maximumNumberOfImageBadges ?? 4}
                                    />
                                )}
                                {/* XNG-Changes */}
                                {product.properties?.legacyManufacturer && (
                                    <LegacyManufacturer
                                        legacyManufacturer={product.properties.legacyManufacturer}
                                        legacyManufacturerLink={product.properties.legacyManufacturerDetailUrl}
                                        isAuthorized={parseBoolean(
                                            product.properties.isLegacyManufacturerAnAuthorizedSupplier,
                                        )}
                                        extendedStyles={styles.legacyManufacturerStyles}
                                    />
                                )}
                            </StyledWrapper>
                        </ProductGridContainer.Info>
                    </StyledWrapper>
                    <StyledWrapper {...styles.priceAndActionsWrapper}>
                        <ProductPriceAndActions
                            showAddToCartActions={showAddToCart}
                            onAddToCartClick={() => trackProductRecommendation(false)}
                            showAvailability={showAvailability}
                            showAddToList={showAddToList}
                            showPrice={showPrice}
                            extendedStyles={styles.productPriceAndActionsStyle}
                        />
                    </StyledWrapper>
                </ProductGridContainer>
                {actionsTemplate ? actionsTemplate(product) : []}
            </ProductContext.Provider>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductCarouselProduct);
